import React, { useEffect, useRef } from 'react';
import {
  DocumentTextIcon,
  BookmarkIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface ChatContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  onRename?: () => void;
  onDelete?: () => void;
  onPin?: () => void;
  onSummary?: () => void;
}

interface MenuItem {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
  disabled: boolean;
}

// Click-outside and escape key handling
function useDismissOnOutsideClick(
  isOpen: boolean,
  onClose: () => void,
  menuRef: React.RefObject<HTMLDivElement>,
) {
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, menuRef]);
}

function ChatContextMenuItem({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  return (
    <button
      onClick={() => {
        if (!item.disabled && item.onClick) {
          item.onClick();
          onClose();
        }
      }}
      disabled={item.disabled}
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
        item.disabled
          ? 'text-gray-400 cursor-not-allowed'
          : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
      }`}
    >
      <item.icon className="h-4 w-4" />
      <span>{item.label}</span>
    </button>
  );
}

export default function ChatContextMenu({
  isOpen,
  onClose,
  position,
  onRename,
  onDelete,
  onPin,
  onSummary,
}: ChatContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(isOpen, onClose, menuRef);

  if (!isOpen) return null;

  const menuItems = ([
    { label: 'Summary', icon: DocumentTextIcon, onClick: onSummary, disabled: !onSummary },
    { label: 'Pin', icon: BookmarkIcon, onClick: onPin, disabled: !onPin },
    { label: 'Rename', icon: PencilSquareIcon, onClick: onRename, disabled: !onRename },
    { label: 'Delete', icon: TrashIcon, onClick: onDelete, disabled: !onDelete },
  ] satisfies MenuItem[]).filter((item) => !item.disabled);
  // No handlers means no actions; don't open an empty popup.
  if (menuItems.length === 0) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white rounded-lg shadow-lg ring-1 ring-black/5 z-50 py-1 w-48"
      style={{ top: position.top, left: position.left }}
    >
      {menuItems.map((item) => (
        <ChatContextMenuItem key={item.label} item={item} onClose={onClose} />
      ))}
    </div>
  );
}
